import { Copy, Mail, MessageSquare, Phone, Video } from "lucide-react";
import type { ContactDetail } from "@hub/shared";
import { Button, Menu, toast } from "@/ui";
import { getHost, hostMode } from "@/engine/transport";

/** Opens a tel:, sms:, facetime: or mailto: link through the desktop shell when present, else the browser. */
export function openReach(url: string): void {
  const host = getHost();
  if (hostMode() === "desktop" && host?.shell?.openExternal) {
    host.shell.openExternal(url).catch(() => toast("Could not open that. The app for it may be missing.", "danger"));
    return;
  }
  window.open(url, "_self");
}

async function copyText(text: string, what: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast(`${what} copied.`, "success");
  } catch {
    toast(`Could not copy the ${what.toLowerCase()}.`, "danger");
  }
}

/** The ways to reach a number: a plain call, FaceTime either way, or a message. Exported for the action palette. */
export function phoneReachItems(value: string) {
  return [
    { id: "call", label: "Call", icon: <Phone size={14} />, onSelect: () => openReach(`tel:${value}`) },
    { id: "facetime", label: "FaceTime", icon: <Video size={14} />, onSelect: () => openReach(`facetime:${value}`) },
    { id: "facetime-audio", label: "FaceTime audio", icon: <Phone size={14} />, onSelect: () => openReach(`facetime-audio:${value}`) },
    { id: "message", label: "Message", icon: <MessageSquare size={14} />, onSelect: () => openReach(`sms:${value}`) },
    { id: "d", label: "", divider: true },
    { id: "copy", label: "Copy number", icon: <Copy size={14} />, onSelect: () => void copyText(value, "Number") },
  ];
}

export function emailReachItems(value: string) {
  return [
    { id: "compose", label: "Mail", icon: <Mail size={14} />, onSelect: () => openReach(`mailto:${value}`) },
    { id: "d", label: "", divider: true },
    { id: "copy", label: "Copy address", icon: <Copy size={14} />, onSelect: () => void copyText(value, "Address") },
  ];
}

/**
 * A phone number or email address on a person's card. Clicking it asks how to reach them instead of guessing: a
 * number can be called, FaceTimed or messaged, an address composed to, and either copied.
 */
export function ReachLink({ kind, value }: { kind: "phone" | "email"; value: string }) {
  return (
    <Menu
      trigger={
        <button type="button" className="u-link" style={{ font: "inherit", background: "transparent", border: 0, padding: 0, cursor: "pointer", color: "var(--accent)" }} title={kind === "phone" ? "Call, FaceTime or message" : "Compose or copy"} data-testid={`reach-${kind}`}>
          {value}
        </button>
      }
      items={kind === "phone" ? phoneReachItems(value) : emailReachItems(value)}
    />
  );
}

const REACH = [
  { id: "message", label: "Message", kind: "phone", icon: <MessageSquare size={13} />, url: (v: string) => `sms:${v}` },
  { id: "call", label: "Call", kind: "phone", icon: <Phone size={13} />, url: (v: string) => `tel:${v}` },
  { id: "mail", label: "Mail", kind: "email", icon: <Mail size={13} />, url: (v: string) => `mailto:${v}` },
  { id: "facetime", label: "FaceTime", kind: "phone", icon: <Video size={13} />, url: (v: string) => `facetime:${v}` },
] as const;

/**
 * The phone's row of ways to reach someone, in the same order: Message, Call, Mail, FaceTime. One number or address
 * goes straight there; several ask which; none leaves the button dimmed.
 */
export function ReachBar({ lookups }: { lookups: ContactDetail["lookups"] }) {
  const values = (kind: "phone" | "email") => lookups.filter((l) => l.lookup.type === kind).sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary)).map((l) => l.lookup.value);
  return (
    <div className="u-row" style={{ gap: 6, flexWrap: "wrap" }} data-testid="reach-bar">
      {REACH.map((r) => {
        const list = values(r.kind);
        if (list.length <= 1) {
          return <Button key={r.id} size="sm" icon={r.icon} disabled={list.length === 0} onClick={() => openReach(r.url(list[0]))}>{r.label}</Button>;
        }
        return (
          <Menu
            key={r.id}
            trigger={<Button size="sm" icon={r.icon}>{r.label}</Button>}
            items={list.map((v) => ({ id: v, label: v, onSelect: () => openReach(r.url(v)) }))}
          />
        );
      })}
    </div>
  );
}
